/**
 * "A load has genuinely settled for a given window" broadcast.
 *
 * The empty-state gate must never fire on the mere absence of loading. During a
 * cold load the chart issues several fetches; the early ones are aborted as they
 * are superseded, and `isLoading` blips false in the gap between an aborted fetch
 * settling and the next one starting (the first fetch takes the `immediate` path,
 * whose `finally` lowers loading even when it was superseded; `isProcessingData`
 * toggles the same way across overlapping calls). That gap can outlast the
 * flicker debounce, so a naive "not loading and no points" test asserts
 * "No data" for a few frames before the real data lands.
 *
 * The distinction that matters lives in the data layer, which alone knows whether
 * a fetch reached a real terminal outcome (success or error) or was merely
 * aborted in favour of a newer one. It publishes that outcome here, keyed by the
 * window it settled for. The empty-state consumer only trusts "No data" once the
 * load for the window it is currently rendering has settled.
 *
 * Module-level rather than React context, because the producer
 * (`useTimeSeriesDataProcessing`, mounted inside `ScatterplotContext`) and the
 * consumer (`TimeSeriesScatterplotContent`) share no prop/context channel for
 * this signal. This mirrors `chartViewportSignal` and the `processDataPerf`
 * store, which cross the same boundary the same way.
 *
 * Keyed rather than a bare boolean so a stale settle from a superseded (or
 * previous-mount) window cannot be mistaken for the current one: the consumer
 * compares the settled key against the key it is rendering, so a window change
 * implicitly "resets" the gate — the keys stop matching — with no explicit reset
 * step. `null` means "a load is in flight / nothing has settled", which never
 * matches any window and therefore keeps the loader up.
 */

let settledKey: string | null = null
const listeners = new Set<() => void>()

/**
 * Stable identity for a logical load: its pen set over its window. Derived from
 * the same inputs the consumer and producer both hold (selected pen ids and the
 * time range's ISO endpoints), so both sides compute an identical key without
 * having to reproduce the request's derived interval.
 */
export const buildLoadSettledKey = (
  sensorIds: string[],
  from: string,
  to: string
): string => `${[...sensorIds].sort().join(',')}::${from}::${to}`

export const getSettledLoadKey = (): string | null => settledKey

/**
 * Publish a settle outcome. Pass the window's key when a fetch reaches a real
 * terminal outcome (success or error) or the range legitimately has no pens;
 * pass `null` when a load starts, to retract any previous settle so the gate
 * stays closed until this load finishes.
 */
export const setSettledLoadKey = (key: string | null): void => {
  if (settledKey === key) return
  settledKey = key
  listeners.forEach((listener) => listener())
}

export const subscribeLoadSettled = (listener: () => void): (() => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Test seam / consumer-unmount cleanup. */
export const resetLoadSettled = (): void => {
  settledKey = null
}

/**
 * The empty-state predicate, extracted so the state machine is testable without
 * a chart. "No data" is asserted only when nothing is drawn, nothing is loading,
 * AND the load for the window currently being rendered has actually settled —
 * never on a transient loading gap whose window has not settled yet.
 */
export const shouldAssertEmptyState = (params: {
  hasDataPoints: boolean
  isChartLoading: boolean
  currentLoadKey: string
  settledLoadKey: string | null
}): boolean =>
  !params.hasDataPoints &&
  !params.isChartLoading &&
  params.settledLoadKey === params.currentLoadKey
